import { v4 as uuidv4 } from 'uuid';
import { Conjunction, Operator, QueryExpression } from '../../../types/constants/query-expression';

type ParseTokensState = {
  expressions: QueryExpression[];
  index: number;
};

type ParseTokensResult = {
  expressions: QueryExpression[];
  nextIndex: number;
};

type TokenizeState = {
  tokens: string[];
  current: string;
  inQuotes: boolean;
  quoteChar: string;
  skip: number;
};

const openBracketCharacter = '(';
const closeBracketCharacter = ')';
const singleQuoteCharacter = "'";
const doubleQuoteCharacter = '"';
const spaceCharacter = ' ';

export const getDeepestLastExpression = (expression: QueryExpression): QueryExpression | undefined => {
  if (expression.expressions && Array.isArray(expression.expressions) && expression.expressions.length > 0) {
    return getDeepestLastExpression(expression.expressions[expression.expressions.length - 1]);
  }
  return expression;
};

const tokenizeQueryString = (query: string) => {
  const operators = Object.values(Operator);

  const result = Array.from(query).reduce<TokenizeState>(
    (state, char, index) => {
      if (state.skip > 0) {
        return { ...state, skip: state.skip - 1 };
      }

      const isQuoteCharacter = char === singleQuoteCharacter || char === doubleQuoteCharacter;
      const isNotEscaped = index === 0 || query[index - 1] !== '\\';

      if (isQuoteCharacter && isNotEscaped) {
        if (!state.inQuotes) {
          return {
            ...state,
            current: state.current + char,
            inQuotes: true,
            quoteChar: char,
          };
        }
        if (char === state.quoteChar) {
          return {
            ...state,
            current: state.current + char,
            inQuotes: false,
            quoteChar: '',
          };
        }
      }

      const isBracketCharacter = char === openBracketCharacter || char === closeBracketCharacter;

      if (!state.inQuotes && isBracketCharacter) {
        const newTokens = state.current.trim()
          ? [...state.tokens, state.current.trim(), char]
          : [...state.tokens, char];
        return {
          ...state,
          tokens: newTokens,
          current: '',
        };
      }

      const isSpaceCharacter = char === spaceCharacter;

      if (!state.inQuotes && isSpaceCharacter) {
        const newTokens = state.current.trim() ? [...state.tokens, state.current.trim()] : state.tokens;
        return {
          ...state,
          tokens: newTokens,
          current: '',
        };
      }

      if (!state.inQuotes) {
        const foundOperator = operators.find((op) => query.substring(index, index + op.length) === op);

        if (foundOperator) {
          const newTokens = state.current.trim()
            ? [...state.tokens, state.current.trim(), foundOperator]
            : [...state.tokens, foundOperator];
          return {
            ...state,
            tokens: newTokens,
            current: '',
            skip: foundOperator.length - 1,
          };
        }
      }

      return {
        ...state,
        current: state.current + char,
      };
    },
    { tokens: [], current: '', inQuotes: false, quoteChar: '', skip: 0 },
  );

  return result.current.trim() ? [...result.tokens, result.current.trim()] : result.tokens;
};

const isConjunctionToken = (token: string) =>
  token.toUpperCase() === Conjunction.AND || token.toUpperCase() === Conjunction.OR;

const createGroupExpression = (nestedExpressions: QueryExpression[]): QueryExpression => ({
  id: uuidv4(),
  property: '',
  value: '',
  operator: '',
  expressions: nestedExpressions,
});

const removeQuotesFromValue = (value: string) => {
  if (value && (value.startsWith(singleQuoteCharacter) || value.startsWith(doubleQuoteCharacter))) {
    return value.slice(1, -1);
  }
  return value;
};

const createPropertyExpression = (property: string, operator: string, value: string): QueryExpression => ({
  id: uuidv4(),
  property,
  operator,
  value: removeQuotesFromValue(value),
});

const attachConjunctionToDeepestExpression = (
  expression: QueryExpression,
  conjunctionToken: string,
): QueryExpression => {
  if (!expression.expressions || expression.expressions.length === 0) {
    return {
      ...expression,
      conjunction: conjunctionToken.toUpperCase() as Conjunction,
    };
  }

  const lastExpressionIndex = expression.expressions.length - 1;
  const updatedLastExpression = attachConjunctionToDeepestExpression(
    expression.expressions[lastExpressionIndex],
    conjunctionToken,
  );

  return {
    ...expression,
    expressions: [...expression.expressions.slice(0, lastExpressionIndex), updatedLastExpression],
  };
};

const buildGroupExpressionPart = (
  expression: QueryExpression,
  isLastInGroup: boolean,
  isLast: boolean,
  buildQuery: (exprs: QueryExpression[], isLast: boolean, isNested: boolean) => string,
) => {
  const nestedQuery = expression.expressions ? buildQuery(expression.expressions, true, true) : null;
  if (!nestedQuery) return '';

  const deepestLastExpr = getDeepestLastExpression(expression);
  const shouldAddConjunction = deepestLastExpr?.conjunction && !(isLastInGroup && isLast);

  return shouldAddConjunction ? `(${nestedQuery}) ${deepestLastExpr.conjunction}` : `(${nestedQuery})`;
};

const buildExpressionPart = (expr: QueryExpression, isLastInGroup: boolean, isLast: boolean, isNested: boolean) => {
  const { property, operator, value } = expr;

  const isValidExpression = (property ?? '').trim().length !== 0 && operator && value && `${value}`.trim().length !== 0;

  if (!isValidExpression) return '';

  const quotedValue = typeof value === 'string' ? `'${value}'` : value;
  const baseExpression = `${property} ${operator} ${quotedValue}`;
  const shouldAddConjunction = expr.conjunction && !((isNested && isLastInGroup) || (isLastInGroup && isLast));

  return shouldAddConjunction ? `${baseExpression} ${expr.conjunction}` : baseExpression;
};

const handleOpeningBracket = (
  state: ParseTokensState,
  tokens: string[],
  depth: number,
  buildExpressionTree: (tokens: string[], depth: number) => ParseTokensResult,
) => {
  const { expressions: nestedExpressions, nextIndex } = buildExpressionTree(tokens.slice(state.index + 1), depth + 1);
  let groupExpression = createGroupExpression(nestedExpressions);
  let newIndex = state.index + nextIndex + 1;

  if (newIndex < tokens.length && isConjunctionToken(tokens[newIndex])) {
    groupExpression = attachConjunctionToDeepestExpression(groupExpression, tokens[newIndex]);
    newIndex += 1;
  }

  const newExpressions = [...state.expressions, groupExpression];

  return { expressions: newExpressions, index: newIndex };
};

const handlePropertyExpression = (state: ParseTokensState, tokens: string[]) => {
  const operators = Object.values(Operator);

  let operatorIndex = state.index;
  while (operatorIndex < tokens.length && !operators.includes(tokens[operatorIndex] as Operator)) {
    operatorIndex += 1;
  }

  if (operatorIndex >= tokens.length) {
    return { expressions: state.expressions, index: state.index + 1 };
  }

  const propertyTokens = tokens.slice(state.index, operatorIndex);
  const property = propertyTokens.join(' ');
  const operator = tokens[operatorIndex];
  const value = tokens[operatorIndex + 1];

  let expression = createPropertyExpression(property, operator, value);
  let nextTokenIndex = operatorIndex + 2;

  if (nextTokenIndex < tokens.length && isConjunctionToken(tokens[nextTokenIndex])) {
    expression = {
      ...expression,
      conjunction: tokens[nextTokenIndex].toUpperCase() as Conjunction,
    };
    nextTokenIndex += 1;
  }

  const newExpressions = [...state.expressions, expression];

  return { expressions: newExpressions, index: nextTokenIndex };
};

const buildExpressionTree = (tokens: string[], depth = 0) => {
  let state: ParseTokensState = { expressions: [], index: 0 };

  while (state.index < tokens.length) {
    const token = tokens[state.index];

    if (token === openBracketCharacter) {
      state = handleOpeningBracket(state, tokens, depth, buildExpressionTree);
      continue;
    }

    if (token === closeBracketCharacter) {
      return {
        expressions: state.expressions,
        nextIndex: state.index + 1,
      };
    }

    if (isConjunctionToken(token)) {
      state = { ...state, index: state.index + 1 };
      continue;
    }

    state = handlePropertyExpression(state, tokens);
  }

  return { expressions: state.expressions, nextIndex: state.index };
};

const buildQuery = (exprs: QueryExpression[], isLast = false, isNested = false): string => {
  const parts = exprs
    .map((expression, index) => {
      const isLastInGroup = index === exprs.length - 1;

      if (expression.expressions && Array.isArray(expression.expressions)) {
        return buildGroupExpressionPart(expression, isLastInGroup, isLast, buildQuery);
      }

      return buildExpressionPart(expression, isLastInGroup, isLast, isNested);
    })
    .filter((part) => part.trim() !== '');

  return parts.join(spaceCharacter);
};

export const parseQueryToExpressions = (query: string) => {
  if ((query ?? '').trim().length === 0) return [];

  try {
    const tokens = tokenizeQueryString(query.trim());
    const { expressions } = buildExpressionTree(tokens, 0);
    return expressions;
  } catch (error) {
    console.error('Error parsing query string:', query, error);
    return [];
  }
};

export const parseExpressionsToQuery = (expressions: QueryExpression[]): string => {
  if (!expressions || expressions.length === 0) return '';

  try {
    return buildQuery(expressions, true, false);
  } catch (error) {
    console.error('Error building query string from expressions:', expressions, error);
    return '';
  }
};
